import { useId, useState } from 'react';

import { cmsLinkedDocument, cmsLinkedPage } from '@shared/cms/links';
import type { SiteCmsCompetitionCard, SiteCmsDocumentResponse, SiteCmsPageDraft } from '@shared/types/entities';
import { Link2 } from 'lucide-react';

import { SiteCmsDialog } from './SiteCmsDialog';
import { type CmsLinkKind, cmsLinkKind, cmsSameDestination, cmsSearchMatches } from './site-cms-interaction-model';
import { useCmsPendingForm } from './use-cms-interactions';

type Props = {
  value: string;
  onChange: (href: string) => void;
  pages: SiteCmsPageDraft[];
  documents: SiteCmsDocumentResponse[];
  competitions: SiteCmsCompetitionCard[];
  label?: string;
  optional?: boolean;
};
export default function SiteCmsLinkPicker(props: Props) {
  const [open, setOpen] = useState(false);
  const page = props.pages.find(p => p.slug === cmsLinkedPage(props.value));
  const doc = props.documents.find(d => d.slug === cmsLinkedDocument(props.value));
  const competition = props.competitions.find(c => c.href === props.value);
  return (
    <div className='cms-link-field'>
      <span className='cms-field-label'>{props.label ?? 'Destination'}</span>
      <div className='cms-row'>
        <span className='cms-link-value'>
          {(page?.title ?? doc?.title ?? competition?.title ?? props.value) || 'Aucune destination'}
        </span>
        <button type='button' className='cms-button' onClick={() => setOpen(true)}>
          <Link2 size={15} />
          Choisir un lien
        </button>
        {props.optional && props.value && (
          <button type='button' className='cms-link-button' onClick={() => props.onChange('')}>
            Retirer
          </button>
        )}
      </div>
      {open && <LinkChooser {...props} onClose={() => setOpen(false)} />}
    </div>
  );
}
function LinkChooser({ value, onChange, pages, documents, competitions, onClose }: Props & { onClose: () => void }) {
  const id = useId();
  const initialType = cmsLinkKind(value, competitions);
  const [type, setType] = useState<CmsLinkKind>(value ? initialType : 'page');
  // Keep a separate candidate per category: a round-trip must not delete a typed URL.
  const [candidates, setCandidates] = useState<Partial<Record<CmsLinkKind, string>>>({ [initialType]: value });
  const target = candidates[type] ?? '';
  const [search, setSearch] = useState('');
  const [consentFor, setConsentFor] = useState<string | null>(null);
  const [error, setError] = useState('');
  const setTarget = (next: string) => {
    setCandidates(old => ({ ...old, [type]: next }));
    setError('');
  };
  useCmsPendingForm(`Destination de lien:${id}`, target !== value);
  const options =
    type === 'page'
      ? pages.map(p => ({
          value: p.slug === 'home' ? '/' : `/${p.slug}/`,
          label: p.title,
          detail: p.visibility === 'DRAFT' ? 'Page privée — lien non publiable' : 'Page du site',
          disabled: p.visibility === 'DRAFT',
        }))
      : type === 'pdf'
        ? documents.map(d => ({
            value: `/documents/${d.slug}.pdf`,
            label: d.title,
            detail: `${Math.ceil(d.sizeBytes / 1024)} Ko · deviendra public avec cette page`,
            disabled: false,
          }))
        : competitions.map(c => ({ value: c.href, label: c.title, detail: c.location, disabled: false }));
  const selected = options.find(o => cmsSameDestination(type, target, o.value));
  const filtered = options.filter(o => cmsSearchMatches(`${o.label} ${o.detail}`, search));
  const hasSuffix = /[?#]/.test(target) && (type === 'page' || type === 'pdf');
  function apply() {
    if (type !== 'external' && (!selected || selected.disabled)) {
      setError('Choisissez une destination disponible pour publication.');
      return;
    }
    if (type === 'pdf' && consentFor !== target) {
      setError('Confirmez la diffusion de ce document.');
      return;
    }
    let result = target;
    if (type === 'external') {
      try {
        const url = new URL(target.trim());
        if (url.protocol !== 'https:' || url.username || url.password || /\s|\\/.test(target.trim())) throw new Error();
        result = url.href;
      } catch {
        setError('Saisissez une adresse HTTPS complète, sans espaces ni identifiants.');
        return;
      }
    }
    onChange(result);
    onClose();
  }
  return (
    <SiteCmsDialog title='Choisir une destination' onClose={onClose}>
      <div className='cms-segmented' role='group' aria-label='Type de destination'>
        {(
          [
            ['page', 'Page du site'],
            ['pdf', 'Document PDF'],
            ['competition', 'Concours'],
            ['external', 'Site externe'],
          ] as const
        ).map(([kind, title]) => (
          <button
            key={kind}
            type='button'
            aria-pressed={type === kind}
            onClick={() => {
              setType(kind);
              setSearch('');
              setError('');
            }}
          >
            {title}
          </button>
        ))}
      </div>
      {type === 'external' ? (
        <label className='cms-field-label'>
          Adresse HTTPS
          <input
            autoFocus
            type='url'
            value={target}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={e => setTarget(e.target.value)}
            placeholder='https://exemple.fr/'
          />
        </label>
      ) : (
        <>
          <label className='cms-field-label'>
            Rechercher
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Nom de la page, du document ou du concours'
            />
          </label>
          {selected && (
            <p className='cms-selection-summary'>
              Sélection : <strong>{selected.label}</strong>
              {hasSuffix && (
                <small>
                  Le fragment ou les paramètres du lien existant sont conservés : <code>{target}</code>.
                </small>
              )}
            </p>
          )}
          {target && (!selected || selected.disabled) && (
            <p className='cms-alert' role='alert'>
              {selected
                ? 'Cette page est privée. Choisissez une autre destination ou rendez-la publique dans Pages.'
                : 'La destination précédente n’est plus disponible. Sélectionnez sa remplaçante.'}
            </p>
          )}
          <div className='cms-choice-list'>
            {filtered.map(o => (
              <label key={o.value} className={o.disabled ? 'is-disabled' : ''}>
                <input
                  type='radio'
                  name={`cms-link-${id}`}
                  checked={cmsSameDestination(type, target, o.value)}
                  disabled={o.disabled}
                  onChange={() => {
                    setTarget(o.value);
                    setConsentFor(null);
                  }}
                />
                <span>
                  <strong>{o.label}</strong>
                  <small>{o.detail}</small>
                </span>
              </label>
            ))}
          </div>
          {!filtered.length && (
            <div className='cms-empty' role='status'>
              <p>
                {options.length
                  ? 'Aucun résultat pour cette recherche.'
                  : 'Aucune destination disponible dans cette catégorie.'}
              </p>
              {search && (
                <button type='button' className='cms-link-button' onClick={() => setSearch('')}>
                  Effacer la recherche
                </button>
              )}
            </div>
          )}
        </>
      )}
      {type === 'pdf' && (
        <label className='cms-consent'>
          <input
            type='checkbox'
            disabled={!selected}
            checked={consentFor === target && Boolean(target)}
            onChange={e => setConsentFor(e.target.checked ? target : null)}
          />
          Je souhaite rendre ce PDF public lors de la publication de cette page.
        </label>
      )}
      {error && (
        <p id={`${id}-error`} role='alert' className='cms-alert'>
          {error}
        </p>
      )}
      <footer className='cms-dialog-actions'>
        <button type='button' className='cms-button' onClick={onClose}>
          Annuler
        </button>
        <button type='button' className='cms-button cms-button-primary' onClick={apply}>
          Utiliser cette destination
        </button>
      </footer>
    </SiteCmsDialog>
  );
}
